import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import lmsApi from '../../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

interface LMSStaffLoginProps {
    onLoginSuccess?: () => void;
}

const LMSStaffLogin: React.FC<LMSStaffLoginProps> = ({ onLoginSuccess }) => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState('');
    const [step, setStep] = useState<'PHONE' | 'OTP'>('PHONE');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await lmsApi.post('/auth/send-otp/', { phone_number: phoneNumber });
            toast.success('OTP Sent Successfully');
            setStep('OTP');
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to send OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await lmsApi.post('/auth/verify-otp/', {
                phone_number: phoneNumber,
                otp: otp,
                is_staff_portal: true
            });
            // We are using Session Cookies, no need to store token in localStorage
            toast.success('Login Successful');
            if (onLoginSuccess) {
                onLoginSuccess();
            } else {
                navigate('/lms/staff/dashboard');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Invalid OTP');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-slate-50 p-4">
            <Card className="w-full max-w-md shadow-lg border-t-4 border-t-primary">
                <CardHeader>
                    <CardTitle className="text-2xl font-bold text-center text-primary">Staff Portal Login</CardTitle>
                    <CardDescription className="text-center">Internal Access - Intellend Staff</CardDescription>
                </CardHeader>
                <CardContent>
                    {step === 'PHONE' ? (
                        <form onSubmit={handleSendOtp} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="phone">Staff Phone Number</Label>
                                <Input
                                    id="phone"
                                    placeholder="+1234567890"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    required
                                />
                            </div>
                            <Button type="submit" className="w-full" disabled={loading}>
                                {loading ? 'Sending...' : 'Send OTP'}
                            </Button>
                        </form>
                    ) : (
                        <form onSubmit={handleVerifyOtp} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="otp">Enter Staff OTP</Label>
                                <Input
                                    id="otp"
                                    placeholder="123456"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    required
                                />
                            </div>
                            <Button type="submit" className="w-full" disabled={loading}>
                                {loading ? 'Verifying...' : 'Verify & Login'}
                            </Button>
                            <Button
                                variant="ghost"
                                className="w-full mt-2"
                                onClick={() => setStep('PHONE')}
                                type="button"
                            >
                                Back to Phone
                            </Button>
                        </form>
                    )}
                </CardContent>
                <CardFooter className="justify-center border-t py-4">
                    <p className="text-xs text-gray-400">Intellend LMS Security Protocol</p>
                </CardFooter>
            </Card>
        </div>
    );
};

export default LMSStaffLogin;
